import { Container } from '@acies/core';
import { DropdownEvent, InputField, InputFieldEvent, Spacer } from '@acies/ui';
import { ModelPosition, StockPosition, TextPosition } from '../../../domain/positions';
import type { SearchResult } from '../../../domain/search-results';
import { searchCommonTexts } from '../../../services/common-texts';
import { searchConstructionModels } from '../../../services/construction-models';
import { searchInventoryItems } from '../../../services/inventory-items';
import { ConfiguratorModel } from '../configurator.model';
import SearchAutoComplete from '../shared/search-auto-complete/search-auto-complete';
import SearchAutoCompleteEvent from '../shared/search-auto-complete/search-auto-complete-event';
import CreatePositionRendererEvent from './create-position-renderer-event';
import ModelTypeDropdown from './model-type-dropdown';
import { toCountable, toPositionNumber, toPrice } from './position-field-values';

export default class CreatePositionRenderer extends Container {
	#checkSpacer!: Spacer;
	/**
	 * The model, item or common text the key field names, or null while it names none the row knows of.
	 *
	 * Set when one is picked in the key field — or, for a text row, found for a key typed into it — and dropped as soon as the key moves away from it, so it is only ever the one the key field names. It is what the field's text is read back to a key by: the field shows the code as a user reads it, and the position is
	 * created with the code as the engine wrote it — see #key. On a text row, holding one also writes its name into {@link CreatePositionRenderer.descriptionInputField}; dropping one leaves the description as it is.
	 */
	#keyItem: SearchResult | null = null;
	#countInputField!: InputField;
	#descriptionInputField!: InputField;
	#descriptionSpacer!: Spacer;
	#heightInputField!: InputField;
	#litraInputField!: InputField;
	#menuSpacer!: Spacer;
	#modelTypeDropdown!: ModelTypeDropdown;
	#noteSpacer!: Spacer;
	#positionInputField!: InputField;
	#priceInputField!: InputField;
	#priceSpacer!: Spacer;
	#searchModelAutoComplete!: SearchAutoComplete<SearchResult>;
	#statusSpacer!: Spacer;
	#widthInputField!: InputField;
	public constructor() {
		super();
		this.width = 'fill';
		this.height = 32;
		this.gapHorizontal = 16;
		this.paddingHorizontal = 16;
		this.alignment = 'left';
	}

	public get checkSpacer(): Spacer {
		if (!this.#checkSpacer) {
			this.#checkSpacer = new Spacer(15, 15);
		}
		return this.#checkSpacer;
	}

	public get positionInputField(): InputField {
		if (!this.#positionInputField) {
			this.#positionInputField = new InputField();
			// where a position sits is a number, so the field takes digits and one dot and nothing
			// else. How small that number may be is not something typing can enforce — 0 is on the
			// way to 0.5 as much as to 42 — so the row judges the value it ends up holding
			this.#positionInputField.type = 'numeric';
			this.#positionInputField.negatives = false;
			this.#positionInputField.width = 56;
			this.#positionInputField.blurOnEnter = false;
		}
		return this.#positionInputField;
	}

	public get modelTypeDropdown(): ModelTypeDropdown {
		if (!this.#modelTypeDropdown) {
			this.#modelTypeDropdown = new ModelTypeDropdown();
		}
		return this.#modelTypeDropdown;
	}

	public get searchModelAutoComplete(): SearchAutoComplete<SearchResult> {
		if (!this.#searchModelAutoComplete) {
			this.#searchModelAutoComplete = new SearchAutoComplete<SearchResult>();
			// what this row searches while it is a model position, which it starts as: the store's
			// construction models, at the path the account's default engine names. The picker itself
			// knows nothing about stores or accounts — it asks this as the user types and shows what
			// comes back. Picking another type swaps it, see #fitToPositionType
			this.#searchModelAutoComplete.search = searchConstructionModels;
			// the header's own word for the column, on every field the row has one for: an empty row
			// is what the user meets, and the header scrolls out of sight above a long list
			this.#searchModelAutoComplete.placeholder = 'Nøgle';
			this.#searchModelAutoComplete.width = 192;
			// Enter creates the position from any field of the row, and a row it refuses keeps the user
			// in the field they pressed it in — see #entered
			this.#searchModelAutoComplete.inputField.blurOnEnter = false;
		}
		return this.#searchModelAutoComplete;
	}

	public get descriptionSpacer(): Spacer {
		if (!this.#descriptionSpacer) {
			this.#descriptionSpacer = new Spacer();
			this.#descriptionSpacer.minWidth = 88;
			this.#descriptionSpacer.width = 'fill';
		}
		return this.#descriptionSpacer;
	}

	/**
	 * Where a text position's description is written, in the description column the row leaves empty otherwise.
	 *
	 * Shown in place of {@link CreatePositionRenderer.descriptionSpacer} while the row is text, and taken out of the layout otherwise, so one of the two always holds the column — see #fitToPositionType. A model and a stock position are described by the API once they are configured, so the row has nothing to ask them
	 * for here.
	 *
	 * Filled in with the name of the common text the key names, and the user's to change from there, or to write themselves when the row has no key. Required while the row is text: a text position is read by what it says, and one with no description would be an empty line in the list.
	 */
	public get descriptionInputField(): InputField {
		if (!this.#descriptionInputField) {
			this.#descriptionInputField = new InputField();
			this.#descriptionInputField.placeholder = 'Beskrivelse';
			this.#descriptionInputField.minWidth = 88;
			this.#descriptionInputField.width = 'fill';
			this.#descriptionInputField.blurOnEnter = false;
			this.#descriptionInputField.visible = false;
		}
		return this.#descriptionInputField;
	}

	public get heightInputField(): InputField {
		if (!this.#heightInputField) {
			this.#heightInputField = new InputField();
			this.#heightInputField.placeholder = 'Højde';
			this.#heightInputField.type = 'numeric';
			this.#heightInputField.decimals = false;
			this.#heightInputField.negatives = false;
			// a measurement is read against the ones above it, so the digits end where the rows above
			// end — which is also where the header sits. Height, width and count are the three columns
			// the header right aligns, and the only three this row does
			this.#heightInputField.textAlign = 'right';
			this.#heightInputField.width = 72;
			this.#heightInputField.blurOnEnter = false;
			// hidden rather than absent, so the row keeps its columns lined up with the header
			this.#heightInputField.visibility = false;
		}
		return this.#heightInputField;
	}

	public get widthInputField(): InputField {
		if (!this.#widthInputField) {
			this.#widthInputField = new InputField();
			this.#widthInputField.placeholder = 'Bredde';
			this.#widthInputField.type = 'numeric';
			this.#widthInputField.decimals = false;
			this.#widthInputField.negatives = false;
			this.#widthInputField.textAlign = 'right';
			this.#widthInputField.width = 72;
			this.#widthInputField.blurOnEnter = false;
			// hidden rather than absent, so the row keeps its columns lined up with the header
			this.#widthInputField.visibility = false;
		}
		return this.#widthInputField;
	}

	public get countInputField(): InputField {
		if (!this.#countInputField) {
			this.#countInputField = new InputField();
			this.#countInputField.placeholder = 'Antal';
			this.#countInputField.textAlign = 'right';
			this.#countInputField.width = 56;
			this.#countInputField.type = 'numeric';
			this.#countInputField.decimals = false;
			this.#countInputField.negatives = false;
			this.#countInputField.blurOnEnter = false;
			// the field holds the one a row starts with, and a count typed into it replaces that rather
			// than being added to it
			this.#countInputField.selectOnFocus = true;
		}
		return this.#countInputField;
	}

	public get litraInputField(): InputField {
		if (!this.#litraInputField) {
			this.#litraInputField = new InputField();
			this.#litraInputField.placeholder = 'Litra';
			this.#litraInputField.width = 128;
			this.#litraInputField.blurOnEnter = false;
			// hidden rather than absent, so the row keeps its columns lined up with the header
			this.#litraInputField.visibility = false;
		}
		return this.#litraInputField;
	}

	public get noteSpacer(): Spacer {
		if (!this.#noteSpacer) {
			this.#noteSpacer = new Spacer();
			this.#noteSpacer.width = 48;
		}
		return this.#noteSpacer;
	}

	public get statusSpacer(): Spacer {
		if (!this.#statusSpacer) {
			this.#statusSpacer = new Spacer();
			this.#statusSpacer.width = 48;
		}
		return this.#statusSpacer;
	}

	public get priceSpacer(): Spacer {
		if (!this.#priceSpacer) {
			this.#priceSpacer = new Spacer();
			this.#priceSpacer.width = 82;
		}
		return this.#priceSpacer;
	}

	/**
	 * Where a text position's price is typed, in the price column the row leaves empty otherwise.
	 *
	 * Shown in place of {@link CreatePositionRenderer.priceSpacer} while the row is text, and taken out of the layout otherwise, the way the description is. A model and a stock position are priced by the engine once they are configured, so the row has nothing to ask them for here.
	 *
	 * Optional: it starts at nought, and an emptied field is nought too — see `toPrice`. Never marked, since there is nothing it can hold that is not a price: it takes digits and one dot, and makes a lone dot `0.`. A text position is one of itself, so the price typed is what it costs altogether.
	 */
	public get priceInputField(): InputField {
		if (!this.#priceInputField) {
			this.#priceInputField = new InputField();
			this.#priceInputField.placeholder = 'Pris';
			this.#priceInputField.type = 'numeric';
			this.#priceInputField.negatives = false;
			// an amount is read against the ones above it, as the measurements are
			this.#priceInputField.textAlign = 'right';
			this.#priceInputField.width = 82;
			this.#priceInputField.blurOnEnter = false;
			// a price typed into the field replaces the nought it starts with rather than being added to it
			this.#priceInputField.selectOnFocus = true;
			this.#priceInputField.visible = false;
		}
		return this.#priceInputField;
	}

	/**
	 * The room the rows of the list give their menu, kept empty here.
	 *
	 * Where the create button stood, and as wide as it was, so the row's columns stay where they were now that Enter creates the position instead.
	 */
	public get menuSpacer(): Spacer {
		if (!this.#menuSpacer) {
			this.#menuSpacer = new Spacer();
			this.#menuSpacer.width = 32;
		}
		return this.#menuSpacer;
	}

	protected override initialize(): void {
		super.initialize();
		// before the children, so the DropdownEvent the model type raises as it picks its first item
		// is heard: it is dispatched while the dropdown is being added, not after
		this.#addEventListeners();
		this.addComponents([
			this.checkSpacer,
			this.positionInputField,
			this.modelTypeDropdown,
			this.searchModelAutoComplete,
			this.descriptionSpacer,
			this.descriptionInputField,
			this.widthInputField,
			this.heightInputField,
			this.countInputField,
			this.litraInputField,
			this.noteSpacer,
			this.statusSpacer,
			this.priceSpacer,
			this.priceInputField,
			this.menuSpacer,
		]);
		// the row is where a new position is typed, so it starts ready to be typed into: the place
		// in the list is filled in for the user, the count is one until they say otherwise, and the
		// model key is what they enter first
		this.positionInputField.text = `${this.#nextPositionNumber}`;
		this.countInputField.text = defaultQuantity;
		this.searchModelAutoComplete.focus();
	}

	/**
	 * One listener on this renderer catches every way the row can change.
	 *
	 * Every one of these events bubbles, so no child needs a listener of its own. Listening here also means no listener needs binding: `this` is the currentTarget, which is this renderer.
	 */
	#addEventListeners(): void {
		this.addEventListener(DropdownEvent.SELECTED_CHANGED, this.#positionTypeChanged as EventListener);
		this.addEventListener(InputFieldEvent.BLURRED, this.#fieldBlurred as EventListener);
		this.addEventListener(InputFieldEvent.ENTER, this.#entered as EventListener);
		this.addEventListener(SearchAutoCompleteEvent.BLURRED, this.#searchBlurred as EventListener);
		this.addEventListener(SearchAutoCompleteEvent.VALUE_CHANGED, this.#keyChanged as EventListener);
	}

	/** Every field of the row that can be marked as holding a value it cannot be created with. The litra is not among them, since a position may have none. */
	get #fields(): ReadonlyArray<InputField | SearchAutoComplete<SearchResult>> {
		return [this.positionInputField, this.searchModelAutoComplete, this.descriptionInputField, this.widthInputField, this.heightInputField, this.countInputField];
	}

	/**
	 * The fields holding a value the row cannot be created with, or none when it can be.
	 *
	 * The litra is left out on purpose — a position may have none. Everything else the row shows for its type has to be there before there is a position to create, save a text position's key: a text position is read by its description, and the key is only where the description may have come from.
	 *
	 * The model is judged by what the search field holds rather than by what was picked in it. The two are the same once a model is picked, since picking one writes its key into the field, but someone who knows the key can type it and move on without ever opening the popover.
	 */
	get #invalidFields(): ReadonlyArray<InputField | SearchAutoComplete<SearchResult>> {
		const invalid: Array<InputField | SearchAutoComplete<SearchResult>> = [];
		if (!isPositionNumber(this.positionInputField.text) || this.#isPositionNumberTaken) {
			invalid.push(this.positionInputField);
		}
		// a text key that names no common text is only known once the API has been asked, so that one
		// is marked when it answers — see #createTextPosition
		if (!this.#isTextPosition && this.searchModelAutoComplete.text === '') {
			invalid.push(this.searchModelAutoComplete);
		}
		// a text position is the one kind the row describes, so the description is only asked for
		// while the row is one — which is also why the field is only there for it
		if (this.#isTextPosition && toDescription(this.descriptionInputField.text) === null) {
			invalid.push(this.descriptionInputField);
		}
		// a model position is the only kind that is made to a size, so the two measurements are only
		// asked for while the row is one — which is also why they are hidden for the other types
		if (this.#isModelPosition && !isCountable(this.widthInputField.text)) {
			invalid.push(this.widthInputField);
		}
		if (this.#isModelPosition && !isCountable(this.heightInputField.text)) {
			invalid.push(this.heightInputField);
		}
		// a text position is not counted, which is why the count is hidden for it
		if (!this.#isTextPosition && !isCountable(this.countInputField.text)) {
			invalid.push(this.countInputField);
		}
		return invalid;
	}

	/**
	 * Whether the place typed into the row is already taken by a position in the list.
	 *
	 * An empty field reads as 0, which no position can be, so a row that has not been filled in is not reported as a clash.
	 */
	get #isPositionNumberTaken(): boolean {
		const typed = Number(this.positionInputField.text);
		return ConfiguratorModel.positions.collection.some((position) => position.position === typed);
	}

	/**
	 * The place a new position takes: the first one free after the last one in the list.
	 *
	 * Whole even when the position it follows is not, so a row after 7.1 is offered 8. An empty list falls out of the same sum, since the highest of nothing is 0 and the first place is 1.
	 */
	get #nextPositionNumber(): number {
		const highest = ConfiguratorModel.positions.collection.reduce((max, position) => Math.max(max, position.position), 0);
		return Math.floor(highest) + 1;
	}

	/** Whether the row is being filled in as a model position, the one type measured by size. */
	get #isModelPosition(): boolean {
		return this.#selectedPositionType === modelPositionType;
	}

	/** Whether the row is being filled in as a stock position, the one type whose key is an inventory item. */
	get #isStockPosition(): boolean {
		return this.#selectedPositionType === stockPositionType;
	}

	/** Whether the row is being filled in as a text position, the one type whose key is a common text. */
	get #isTextPosition(): boolean {
		return this.#selectedPositionType === textPositionType;
	}

	/** The kind of position the row is being filled in as, or null while no type is picked. */
	get #selectedPositionType(): unknown {
		const { selectedItem } = this.modelTypeDropdown;
		return selectedItem === null ? null : selectedItem.value;
	}

	/**
	 * Creates the position the row has been filled in with, when Enter is pressed in any of its fields.
	 *
	 * The Enter is stopped here and a CreatePositionRendererEvent raised in its place, so what leaves the row is a position rather than a key pressed in one of its fields.
	 *
	 * A row that does not read as a whole position is refused, and every field holding a value it cannot be created with is marked as an error. Nothing is dispatched, and the focus stays in the field Enter was pressed in — no field of the row lets go of it on Enter — so the user goes on from where they were. A mark
	 * stays until its field is focused again — which an InputField answers by taking its own mark off, the search field's included — or the type is changed, see #positionTypeChanged, rather than going the moment the value is mended: it says what was wrong when the row was refused.
	 *
	 * No type picked is a row refused with no field to mark, since the dropdown has no error of its own to show.
	 *
	 * A text row may have to ask the API for the text its key names first — see #createTextPosition.
	 */
	#entered(event: InputFieldEvent): void {
		event.stopPropagation();
		for (const field of this.#invalidFields) {
			field.error = true;
		}
		if (this.#isTextPosition) {
			void this.#createTextPosition();
			return;
		}
		this.#create();
	}

	/**
	 * Creates the text position the row has been filled in with, once it is known which common text its key names, if it has a key at all.
	 *
	 * Known straight away when the text was picked, or found when the key was typed and left, and the position is created in the same turn as the Enter — as it is for a row with no key, which is described by hand. A key typed and entered without leaving the field is looked up first, its name written into the
	 * description as a key left would have it, and the position is created when the API answers — or the key marked, when no text has exactly that code: a key may be left out, but one that is there has to name a text.
	 *
	 * The row may have moved on while the API was asked — another key typed, another type picked, the row created and emptied by a second Enter — and then the answer is dropped where it lands: it was asked about a row that is no longer there.
	 */
	async #createTextPosition(): Promise<void> {
		const key = this.searchModelAutoComplete.text;
		if (key !== '' && !this.#holdsKeyItemFor(key)) {
			const commonText = await this.#findCommonText(key);
			if (!this.#isTextPosition || this.searchModelAutoComplete.text !== key) {
				return;
			}
			if (commonText === null) {
				this.searchModelAutoComplete.error = true;
				return;
			}
			this.#holdKeyItem(commonText);
		}
		this.#create();
	}

	/**
	 * The common text with exactly the code `key`, as written or as read, or null when there is none — or when the search could not be made.
	 *
	 * Asked of the key field's own search, with its own limit, so it is answered from the search the user's typing already made: the code the user typed is among what the popover offered for it, or it is not a text.
	 */
	async #findCommonText(key: string): Promise<SearchResult | null> {
		const { search } = this.searchModelAutoComplete;
		if (search === null || key === '') {
			return null;
		}
		const found = await search(this.searchModelAutoComplete.searchLimit, key);
		return Array.isArray(found) ? (found.find((commonText) => commonText.isKeyedBy(key)) ?? null) : null;
	}

	/** Whether the row holds the one `text` is the code of, as written or as read. */
	#holdsKeyItemFor(text: string): boolean {
		return this.#keyItem !== null && this.#keyItem.isKeyedBy(text);
	}

	/**
	 * Makes `item` the one the key field names. On a text row, its name is also written into the description — over whatever was written there, since picking a text is the user saying which one they want.
	 *
	 * Dropping it leaves the description as it is: a key emptied or typed over is not the user taking back what they wrote.
	 */
	#holdKeyItem(item: SearchResult | null): void {
		this.#keyItem = item;
		if (item !== null && this.#isTextPosition) {
			this.descriptionInputField.text = item.name;
		}
	}

	/** Raises the position the row reads as, and empties the row for the next one — or does nothing, for a row that reads as none. */
	#create(): void {
		// asked whether or not a field was marked: it refuses the same rows the marks are put on, and
		// it is the one that knows what the fields read as
		const position = this.#getNewPositionFromInput();
		if (position !== null) {
			this.dispatchEvent(new CreatePositionRendererEvent(position));
			this.#reset();
			// the emptied row is a new one, and a new row is ready to be typed into
			this.searchModelAutoComplete.focus();
		}
	}

	/**
	 * Marks the place as taken when the user leaves it holding one another position already has.
	 *
	 * Only when the place itself was left: leaving another field says nothing new about whether that place is taken. Only ever setting the mark and never taking it off, for the reason #entered gives: it goes when the field is focused again, which is the way back to mending it — so a place left free still carries a
	 * mark Enter put there.
	 *
	 * The event is stopped here: a change inside the row is this renderer's business, and nothing above it in the DOM has any use for it.
	 */
	#fieldBlurred(event: InputFieldEvent): void {
		event.stopPropagation();
		if (event.target === this.positionInputField && this.#isPositionNumberTaken) {
			this.positionInputField.error = true;
		}
	}

	/**
	 * Fits the row to the type the user picked, and takes every mark off it.
	 *
	 * A mark said what was wrong with the row as the type it was refused as, and the fields asked for are not the same for another: a model position is measured and a stock position is not.
	 */
	#positionTypeChanged(event: DropdownEvent): void {
		event.stopPropagation();
		this.#clearErrors();
		this.#fitToPositionType();
	}

	/**
	 * Stops the search field saying it has been left, which the row has no use for: the field is read when Enter is pressed rather than judged as it changes.
	 *
	 * Stopped all the same, since it has been dealt with here and nothing above the row is meant to hear it.
	 */
	#searchBlurred(event: SearchAutoCompleteEvent): void {
		event.stopPropagation();
	}

	/**
	 * Holds what the user settled on in the key field.
	 *
	 * Whatever is picked in the popover is held at once, and the field is made to show its code as a user reads it — the popover writes the code as the engine wrote it, which may be encoded: `0TS11MODULM%C3%85L` is shown as `0TS11MODULMÅL`. Through the field's text setter, so that is the value the field is now meant
	 * to hold, and leaving it unchanged says nothing. A key typed and left drops what was held for the old key, unless it is that one's code typed back.
	 *
	 * A text row does more with it. A picked text sends the user on to the description it filled in, which is theirs to change before Enter creates the position, and a typed key is looked up, so the description says what Enter will create before it is pressed. A model and a stock row are read when Enter is pressed.
	 *
	 * Stopped in every case, since it has been dealt with here and nothing above the row is meant to hear it.
	 */
	#keyChanged(event: SearchAutoCompleteEvent<SearchResult>): void {
		event.stopPropagation();
		const { selectedItem, text } = event;
		if (selectedItem !== null) {
			this.#holdKeyItem(selectedItem);
			this.searchModelAutoComplete.text = selectedItem.label;
			if (this.#isTextPosition) {
				this.descriptionInputField.focus();
			}
			return;
		}
		// the code of what is held, typed back as it reads, still names it
		if (this.#holdsKeyItemFor(text)) {
			return;
		}
		this.#holdKeyItem(null);
		if (this.#isTextPosition) {
			void this.#describeTypedKey(text);
		}
	}

	/** Holds the common text a typed key names, once the API has said which one — unless the row has moved on to another key or type meanwhile. */
	async #describeTypedKey(key: string): Promise<void> {
		const commonText = await this.#findCommonText(key);
		if (commonText !== null && this.#isTextPosition && this.searchModelAutoComplete.text === key) {
			this.#holdKeyItem(commonText);
		}
	}

	/**
	 * The key the row's position is created with, or null while the key field is empty.
	 *
	 * The field shows a key as a user reads it, and a position refers to its model, item or text by the code the engine wrote into the path — see `SearchResult.label`. So a field naming what the row holds is read back to that one's key, and anything else in it is a key typed straight in, taken as it was typed.
	 */
	get #key(): string | null {
		const { text } = this.searchModelAutoComplete;
		if (text === '') {
			return null;
		}
		return this.#keyItem !== null && this.#keyItem.isKeyedBy(text) ? this.#keyItem.key : text;
	}

	/** Takes the mark off every field of the row. */
	#clearErrors(): void {
		for (const field of this.#fields) {
			field.error = false;
		}
	}

	/**
	 * Reads the row as the position it describes, or null when it describes none.
	 *
	 * Null rather than a thrown error, because a half-filled row is an ordinary state rather than a fault. The rules are the ones #invalidFields marks the fields by, read here off the values themselves: it is these values the position is built from, and a schema class refuses anything it cannot hold.
	 *
	 * A text position is built from its place and its description, trimmed, and from its key, or null when the key field is empty: a key that is there names a common text, which #createTextPosition has made sure of by the time it gets here. It is one of itself, since it is not counted, and has no text: the lines the
	 * common text runs to are not read yet. It is none while it has no description. It is handed over succeeded rather than pending, since nothing is ever going to work it out — it is neither configured nor priced by the engine.
	 */
	#getNewPositionFromInput(): ModelPosition | StockPosition | TextPosition | null {
		// a place another position holds is no place for this one
		const position = this.#isPositionNumberTaken ? null : toPositionNumber(this.positionInputField.text);
		if (position === null) {
			return null;
		}
		if (this.#isTextPosition) {
			const description = toDescription(this.descriptionInputField.text);
			const price = toPrice(this.priceInputField.text);
			if (description === null || price === null) {
				return null;
			}
			// one of itself, so what one costs is what it costs altogether
			return new TextPosition({
				...positionDefaults,
				description,
				key: this.#key,
				position,
				price,
				quantity: 1,
				status: 'succeeded',
				totalPrice: price,
				type: textPositionType,
			});
		}
		const quantity = toCountable(this.countInputField.text);
		if (quantity === null) {
			return null;
		}
		// the picked model's or item's key when one was picked, and a key typed straight in when
		// none was
		const key = this.#key;
		if (key === null) {
			return null;
		}
		const height = toCountable(this.heightInputField.text);
		const shared = { ...positionDefaults, key, position };
		const width = toCountable(this.widthInputField.text);
		if (this.#isModelPosition) {
			if (height === null || width === null) {
				return null;
			}
			return new ModelPosition({
				...shared,
				quantity,
				height,
				litra: toNullOrText(this.litraInputField.text),
				note: null,
				type: modelPositionType,
				width,
			});
		}
		if (this.#isStockPosition) {
			// the configuration the item comes in is picked once the position is in the list, among the
			// ones the engine answers its first configuration with — see `StockConfiguration`
			return new StockPosition({ ...shared, quantity, type: stockPositionType });
		}
		return null;
	}

	/**
	 * Empties the row, so the next position can be typed into it straight away.
	 *
	 * The fields are cleared rather than replaced, so the row keeps its place in the list and the user keeps their cursor. Fitting the row to its type is done here at the end rather than left to the listeners: none of these fire for a value the row sets on itself, and the one that does — the dropdown, which raises a
	 * DropdownEvent when its selection goes — does so partway through.
	 */
	#reset(): void {
		// the count goes back to one rather than to nothing, as the row started
		this.countInputField.text = defaultQuantity;
		this.heightInputField.text = '';
		this.litraInputField.text = '';
		this.modelTypeDropdown.reset();
		// the position that was just created took a place, so the emptied row is offered the next
		this.positionInputField.text = `${this.#nextPositionNumber}`;
		this.widthInputField.text = '';
		this.#clearErrors();
		this.#fitToPositionType();
	}

	/**
	 * Fits the row to the kind of position it is being filled in as: which fields it shows, and what its key is searched in.
	 *
	 * The height, the width and the litra show only while the row is a model position. A stock and a text position have none of the three, so for those the fields have nothing to hold, and a litra typed into one would be dropped without a word. They are hidden rather than removed, so the row's columns stay lined up
	 * with the header whichever type is picked — save on a text row, whose description takes every column up to the price's.
	 *
	 * The key is searched for among the store's inventory items while the row is a stock position, by item number, among its common texts while it is text, by code, and among its construction models while it is a model — see `searchFor`. Swapping the search empties the key field, and forgets what was picked and found
	 * in it: a key names something in the collection the old type named, and means nothing — or something else — in the new one.
	 *
	 * What the key field named is dropped with the type, whichever way it goes, and a text row's description with it: it was found in the collection the old type named.
	 *
	 * The configuration a stock position's item comes in is not picked here: it is picked in the answers section once the position is in the list — see `StockConfiguration`.
	 *
	 * A text position is described in the description column, which reaches over every column up to the price's: a text is longer than a key, and the measurements, the count, the litra, the note and the status are taken out of the layout for it rather than hidden — see
	 * {@link CreatePositionRenderer.descriptionInputField}. It is priced in the price column, which starts at nought again whichever way the type goes — see {@link CreatePositionRenderer.priceInputField}.
	 *
	 * The count shows for a model and a stock position, and is out of the layout for a text position, which is not counted.
	 */
	#fitToPositionType(): void {
		const isModelPosition = this.#isModelPosition;
		const isTextPosition = this.#isTextPosition;
		this.heightInputField.visibility = isModelPosition;
		this.litraInputField.visibility = isModelPosition;
		this.widthInputField.visibility = isModelPosition;
		// out of the layout for a text row, so the description fills every column between it and the
		// price's — and the price's column and the menu's stay where they were, the row being as wide
		this.heightInputField.visible = !isTextPosition;
		this.widthInputField.visible = !isTextPosition;
		this.countInputField.visible = !isTextPosition;
		this.litraInputField.visible = !isTextPosition;
		this.noteSpacer.visible = !isTextPosition;
		this.statusSpacer.visible = !isTextPosition;
		this.descriptionSpacer.visible = !isTextPosition;
		this.descriptionInputField.visible = isTextPosition;
		this.descriptionInputField.text = '';
		this.priceSpacer.visible = !isTextPosition;
		this.priceInputField.visible = isTextPosition;
		this.priceInputField.text = defaultPrice;
		this.#holdKeyItem(null);
		this.searchModelAutoComplete.reset();
		this.searchModelAutoComplete.search = searchFor(this.#selectedPositionType);
	}
}

/** The values ModelTypeDropdown offers each kind under, which are what `type` names them by too. */
const modelPositionType = 'model';
const stockPositionType = 'stock';
const textPositionType = 'text';

/** What the key field searches while the row is `type`: the inventory items for a stock position, the common texts for a text position, and the construction models otherwise — a row with no type picked included, since a new row is a model row. */
function searchFor(type: unknown): SearchAutoComplete<SearchResult>['search'] {
	if (type === stockPositionType) {
		return searchInventoryItems;
	}
	return type === textPositionType ? searchCommonTexts : searchConstructionModels;
}

/** The count a row starts with: one of whatever it is, which is what most positions are, so a model needs only its two measurements before Enter creates it. */
const defaultQuantity = '1';

/** The price a text row starts with: nothing charged, until the user says otherwise. */
const defaultPrice = '0';

/**
 * What a freshly filled in row cannot say for itself.
 *
 * The API prices a model and a stock position once it has it, so the row hands those over free and uncalculated; a text position is priced in the row itself. Where it lands in the list the row does know: that is typed into it.
 */
const positionDefaults = {
	description: null,
	price: 0,
	status: 'pending',
} as const;

/** Whether what the user typed reads as a quantity, a height or a width. See `toCountable`. */
function isCountable(text: string): boolean {
	return toCountable(text) !== null;
}

/** Whether what the user typed reads as a place in the list. See `toPositionNumber`. */
function isPositionNumber(text: string): boolean {
	return toPositionNumber(text) !== null;
}

/** What an optional text field holds, or null when it holds nothing: no field is an empty string. */
function toNullOrText(text: string): string | null {
	return text === '' ? null : text;
}

/** The description a text position is created with: what the field holds, trimmed, or null when that leaves nothing — a description of spaces says nothing either. */
function toDescription(text: string): string | null {
	return toNullOrText(text.trim());
}

customElements.define('cf-create-position-renderer', CreatePositionRenderer);
